import {
  Component, EventEmitter, Input, OnDestroy, OnInit, Output
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  GrupoModificadorDTO,
  GrupoModificadorConsultaDTO,
  OpcaoModificadorDTO,
  OpcaoModificadorConsultaDTO,
  ProdutoGrupoModificadorConsultaDTO,
  ReordenarDTO,
} from '../../../../../../../models/cardapio-minishop/cardapio-minishop.model';
import { CardapioMinishopModificadorService } from '../../../../../../../services/cardapio-minishop/cardapio-minishop-modificador.service';
import { ProdutoGrupoModificadorService } from '../../../../../../../services/cardapio-minishop/produto-grupo-modificador.service';

@Component({
  selector: 'app-modificador-gestao-modal',
  templateUrl: './modificador-gestao-modal.component.html',
  styleUrls: ['./modificador-gestao-modal.component.scss'],
  providers: [MessageService],
})
export class ModificadorGestaoModalComponent implements OnInit, OnDestroy {

  @Input() visivel = false;
  @Input() produtoId = 0;
  @Input() gruposAssociados: ProdutoGrupoModificadorConsultaDTO[] = [];
  @Input() moedaSimbolo = 'BRL';
  @Input() moedaLocal = 'pt-BR';

  @Output() visivelChange = new EventEmitter<boolean>();
  @Output() grupoAssociado = new EventEmitter<ProdutoGrupoModificadorConsultaDTO>();
  @Output() grupoDesassociado = new EventEmitter<number>();
  @Output() grupoAtualizado = new EventEmitter<void>();

  // Estado - lista de grupos
  todosGrupos: GrupoModificadorConsultaDTO[] = [];
  filtroTexto = '';
  loadingGrupos = false;

  // Estado - edicao de grupo
  grupoSelecionado: GrupoModificadorConsultaDTO | null = null;
  novoGrupo = false;
  loadingOpcoes = false;
  salvandoGrupo = false;

  // Formulario do grupo em edicao
  grupoForm: Partial<GrupoModificadorDTO> = {};

  // Nova opcao inline
  novaOpcao: Partial<OpcaoModificadorDTO> | null = null;

  texts = {
    titulo: '',
    descricao: '',
    buscar: '',
    novo_grupo: '',
    associado: '',
    nao_associado: '',
    nenhum_grupo: '',
    editar_grupo: '',
    nome_grupo: '',
    minimo_selecoes: '',
    maximo_selecoes: '',
    obrigatorio: '',
    opcional: '',
    opcoes_titulo: '',
    adicionar_opcao: '',
    nome_opcao: '',
    preco_adicional: '',
    visivel: '',
    custo: '',
    desconto: '',
    sku: '',
    confirmar_excluir_grupo: '',
    confirmar_excluir_opcao: '',
    sucesso_criar_grupo: '',
    sucesso_atualizar_grupo: '',
    sucesso_excluir_grupo: '',
    sucesso_criar_opcao: '',
    sucesso_atualizar_opcao: '',
    sucesso_excluir_opcao: '',
    sucesso_reordenar_opcoes: '',
    voltar: '',
    salvar: '',
    cancelar: '',
    confirmar: '',
    excluir: '',
    erro: '',
    erro_generico: '',
    sucesso_associar: '',
    sucesso_desassociar: '',
    opcoes: '',
  };

  private langChangeSub!: Subscription;

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private modificadorService: CardapioMinishopModificadorService,
    private produtoGrupoService: ProdutoGrupoModificadorService,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) this.langChangeSub.unsubscribe();
  }

  // --- Visibilidade do modal ---

  onShow(): void {
    this.grupoSelecionado = null;
    this.novoGrupo = false;
    this.filtroTexto = '';
    this.carregarGrupos();
  }

  onHide(): void {
    this.visivelChange.emit(false);
  }

  // --- Carregar grupos ---

  carregarGrupos(): void {
    this.loadingGrupos = true;
    this.modificadorService.listarGrupos().subscribe({
      next: (data) => {
        this.todosGrupos = data;
        this.loadingGrupos = false;
      },
      error: (err) => {
        this.handleError(err);
        this.loadingGrupos = false;
      },
    });
  }

  get gruposFiltrados(): GrupoModificadorConsultaDTO[] {
    if (!this.filtroTexto.trim()) return this.todosGrupos;
    const filtro = this.filtroTexto.toLowerCase();
    return this.todosGrupos.filter(g => g.nome.toLowerCase().includes(filtro));
  }

  isAssociado(grupoId: number): boolean {
    return this.gruposAssociados.some(a => a.grupoModificador.id === grupoId);
  }

  getAssociacaoId(grupoId: number): number | null {
    const assoc = this.gruposAssociados.find(a => a.grupoModificador.id === grupoId);
    return assoc ? assoc.id : null;
  }

  // --- Associar / Desassociar ---

  toggleAssociacao(grupo: GrupoModificadorConsultaDTO): void {
    if (this.isAssociado(grupo.id)) {
      const assocId = this.getAssociacaoId(grupo.id);
      if (assocId) {
        this.produtoGrupoService.desassociar(assocId).subscribe({
          next: () => {
            this.grupoDesassociado.emit(assocId);
            this.messageService.add({
              severity: 'success',
              summary: this.texts.sucesso_desassociar,
              life: 3000,
            });
          },
          error: (err) => this.handleError(err),
        });
      }
    } else {
      this.produtoGrupoService.associar({
        produtoId: this.produtoId,
        grupoModificadorId: grupo.id,
        ordem: this.gruposAssociados.length,
      }).subscribe({
        next: (assoc) => {
          this.grupoAssociado.emit(assoc);
          this.messageService.add({
            severity: 'success',
            summary: this.texts.sucesso_associar,
            life: 3000,
          });
        },
        error: (err) => this.handleError(err),
      });
    }
  }

  // --- Criar novo grupo ---

  iniciarNovoGrupo(): void {
    this.novoGrupo = true;
    this.grupoSelecionado = null;
    this.grupoForm = {
      nome: '',
      obrigatorio: false,
      minimoSelecoes: 0,
      maximoSelecoes: 1,
      ordem: this.todosGrupos.length,
    };
  }

  // --- Editar grupo existente ---

  editarGrupo(grupo: GrupoModificadorConsultaDTO): void {
    this.grupoSelecionado = { ...grupo, opcoes: [...grupo.opcoes] };
    this.novoGrupo = false;
    this.grupoForm = {
      nome: grupo.nome,
      obrigatorio: grupo.obrigatorio,
      minimoSelecoes: grupo.minimoSelecoes,
      maximoSelecoes: grupo.maximoSelecoes,
      ordem: grupo.ordem,
    };
  }

  voltarParaLista(): void {
    this.grupoSelecionado = null;
    this.novoGrupo = false;
    this.novaOpcao = null;
    this.carregarGrupos();
  }

  // --- Salvar grupo ---

  salvarGrupo(): void {
    if (!this.grupoForm.nome?.trim()) return;

    this.salvandoGrupo = true;
    const dto: GrupoModificadorDTO = {
      nome: this.grupoForm.nome!.trim(),
      obrigatorio: this.grupoForm.obrigatorio || false,
      minimoSelecoes: this.grupoForm.minimoSelecoes || 0,
      maximoSelecoes: this.grupoForm.maximoSelecoes || 1,
      ordem: this.grupoForm.ordem || 0,
    };

    if (this.novoGrupo) {
      this.modificadorService.criarGrupo(dto).subscribe({
        next: (grupo) => {
          this.salvandoGrupo = false;
          this.messageService.add({
            severity: 'success',
            summary: this.texts.sucesso_criar_grupo,
            life: 3000,
          });
          // Associar automaticamente ao produto
          this.produtoGrupoService.associar({
            produtoId: this.produtoId,
            grupoModificadorId: grupo.id,
            ordem: this.gruposAssociados.length,
          }).subscribe({
            next: (assoc) => {
              this.grupoAssociado.emit(assoc);
              // Abrir para edicao
              this.editarGrupo(grupo);
              this.carregarGrupos();
            },
            error: (err) => this.handleError(err),
          });
        },
        error: (err) => {
          this.salvandoGrupo = false;
          this.handleError(err);
        },
      });
    } else if (this.grupoSelecionado) {
      this.modificadorService.atualizarGrupo(this.grupoSelecionado.id, dto).subscribe({
        next: (grupoAtualizado) => {
          this.salvandoGrupo = false;
          this.messageService.add({
            severity: 'success',
            summary: this.texts.sucesso_atualizar_grupo,
            life: 3000,
          });
          this.grupoSelecionado = { ...grupoAtualizado, opcoes: grupoAtualizado.opcoes || [] };
          this.grupoAtualizado.emit();
          this.carregarGrupos();
        },
        error: (err) => {
          this.salvandoGrupo = false;
          this.handleError(err);
        },
      });
    }
  }

  // --- Excluir grupo ---

  excluirGrupo(): void {
    if (!this.grupoSelecionado) return;
    this.modificadorService.removerGrupo(this.grupoSelecionado.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.texts.sucesso_excluir_grupo,
          life: 3000,
        });
        // Se estava associado, emitir desassociacao
        const assocId = this.getAssociacaoId(this.grupoSelecionado!.id);
        if (assocId) {
          this.grupoDesassociado.emit(assocId);
        }
        this.grupoAtualizado.emit();
        this.voltarParaLista();
      },
      error: (err) => this.handleError(err),
    });
  }

  // --- Opcoes do grupo ---

  iniciarNovaOpcao(): void {
    if (!this.grupoSelecionado) return;
    this.novaOpcao = {
      nome: '',
      precoAdicional: 0,
      grupoModificadorId: this.grupoSelecionado.id,
      visivel: true,
      custo: null,
      desconto: null,
      sku: null,
      ordem: this.grupoSelecionado.opcoes.length,
    };
  }

  cancelarNovaOpcao(): void {
    this.novaOpcao = null;
  }

  salvarNovaOpcao(): void {
    if (!this.novaOpcao || !this.novaOpcao.nome?.trim() || !this.grupoSelecionado) return;

    const dto: OpcaoModificadorDTO = {
      nome: this.novaOpcao.nome!.trim(),
      precoAdicional: this.novaOpcao.precoAdicional || 0,
      grupoModificadorId: this.grupoSelecionado.id,
      visivel: this.novaOpcao.visivel !== false,
      custo: this.novaOpcao.custo || null,
      desconto: this.novaOpcao.desconto || null,
      sku: this.novaOpcao.sku || null,
      ordem: this.novaOpcao.ordem || this.grupoSelecionado.opcoes.length,
    };

    this.modificadorService.criarOpcao(dto).subscribe({
      next: (opcao) => {
        this.grupoSelecionado!.opcoes.push(opcao);
        this.novaOpcao = null;
        this.messageService.add({
          severity: 'success',
          summary: this.texts.sucesso_criar_opcao,
          life: 3000,
        });
        this.grupoAtualizado.emit();
      },
      error: (err) => this.handleError(err),
    });
  }

  salvarOpcao(opcao: OpcaoModificadorConsultaDTO): void {
    const dto: OpcaoModificadorDTO = {
      nome: opcao.nome,
      precoAdicional: opcao.precoAdicional,
      grupoModificadorId: opcao.grupoModificadorId,
      visivel: opcao.visivel,
      custo: opcao.custo || null,
      desconto: opcao.desconto || null,
      sku: opcao.sku || null,
      ordem: opcao.ordem,
    };

    this.modificadorService.atualizarOpcao(opcao.id, dto).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.texts.sucesso_atualizar_opcao,
          life: 3000,
        });
        this.grupoAtualizado.emit();
      },
      error: (err) => this.handleError(err),
    });
  }

  removerOpcao(opcao: OpcaoModificadorConsultaDTO): void {
    this.modificadorService.removerOpcao(opcao.id).subscribe({
      next: () => {
        if (this.grupoSelecionado) {
          this.grupoSelecionado.opcoes = this.grupoSelecionado.opcoes.filter(o => o.id !== opcao.id);
        }
        this.messageService.add({
          severity: 'success',
          summary: this.texts.sucesso_excluir_opcao,
          life: 3000,
        });
        this.grupoAtualizado.emit();
      },
      error: (err) => this.handleError(err),
    });
  }

  onDropOpcao(event: CdkDragDrop<OpcaoModificadorConsultaDTO[]>): void {
    if (!this.grupoSelecionado || event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.grupoSelecionado.opcoes, event.previousIndex, event.currentIndex);
    const ids = this.grupoSelecionado.opcoes.map(o => o.id);
    this.modificadorService.reordenarOpcoes({ ids }).subscribe({
      next: () => {
        this.grupoAtualizado.emit();
      },
      error: (err) => {
        this.handleError(err);
        // Reverter
        moveItemInArray(this.grupoSelecionado!.opcoes, event.currentIndex, event.previousIndex);
      },
    });
  }

  trackByGrupoId(index: number, item: GrupoModificadorConsultaDTO): number {
    return item.id;
  }

  trackByOpcaoId(index: number, item: OpcaoModificadorConsultaDTO): number {
    return item.id;
  }

  // --- Tratamento de erro ---

  private handleError(error: any): void {
    const mensagem = error?.error?.message || this.texts.erro_generico;
    this.messageService.add({
      severity: 'error',
      summary: this.texts.erro,
      detail: mensagem,
      life: 5000,
    });
  }

  private loadTranslations(): void {
    const base = 'cardapio_minishop.modificador';
    const gestao = `${base}.gestao`;
    this.texts = {
      titulo: this.translate.instant(`${gestao}.titulo`),
      descricao: this.translate.instant(`${gestao}.descricao`),
      buscar: this.translate.instant(`${gestao}.buscar`),
      novo_grupo: this.translate.instant(`${gestao}.novo_grupo`),
      associado: this.translate.instant(`${gestao}.associado`),
      nao_associado: this.translate.instant(`${gestao}.nao_associado`),
      nenhum_grupo: this.translate.instant(`${gestao}.nenhum_grupo`),
      editar_grupo: this.translate.instant(`${gestao}.editar_grupo`),
      nome_grupo: this.translate.instant(`${gestao}.nome_grupo`),
      minimo_selecoes: this.translate.instant(`${gestao}.minimo_selecoes`),
      maximo_selecoes: this.translate.instant(`${gestao}.maximo_selecoes`),
      obrigatorio: this.translate.instant(`${base}.obrigatorio`),
      opcional: this.translate.instant(`${base}.opcional`),
      opcoes_titulo: this.translate.instant(`${gestao}.opcoes_titulo`),
      adicionar_opcao: this.translate.instant(`${gestao}.adicionar_opcao`),
      nome_opcao: this.translate.instant(`${gestao}.nome_opcao`),
      preco_adicional: this.translate.instant(`${gestao}.preco_adicional`),
      visivel: this.translate.instant(`${gestao}.visivel`),
      custo: this.translate.instant(`${gestao}.custo`),
      desconto: this.translate.instant(`${gestao}.desconto`),
      sku: this.translate.instant(`${gestao}.sku`),
      confirmar_excluir_grupo: this.translate.instant(`${gestao}.confirmar_excluir_grupo`),
      confirmar_excluir_opcao: this.translate.instant(`${gestao}.confirmar_excluir_opcao`),
      sucesso_criar_grupo: this.translate.instant(`${gestao}.sucesso_criar_grupo`),
      sucesso_atualizar_grupo: this.translate.instant(`${gestao}.sucesso_atualizar_grupo`),
      sucesso_excluir_grupo: this.translate.instant(`${gestao}.sucesso_excluir_grupo`),
      sucesso_criar_opcao: this.translate.instant(`${gestao}.sucesso_criar_opcao`),
      sucesso_atualizar_opcao: this.translate.instant(`${gestao}.sucesso_atualizar_opcao`),
      sucesso_excluir_opcao: this.translate.instant(`${gestao}.sucesso_excluir_opcao`),
      sucesso_reordenar_opcoes: this.translate.instant(`${gestao}.sucesso_reordenar_opcoes`),
      voltar: this.translate.instant(`${gestao}.voltar`),
      salvar: this.translate.instant('cardapio_minishop.comum.salvar'),
      cancelar: this.translate.instant('cardapio_minishop.comum.cancelar'),
      confirmar: this.translate.instant('cardapio_minishop.comum.confirmar'),
      excluir: this.translate.instant('cardapio_minishop.comum.excluir'),
      erro: this.translate.instant('cardapio_minishop.comum.erro'),
      erro_generico: this.translate.instant('cardapio_minishop.comum.erro_generico'),
      sucesso_associar: this.translate.instant(`${base}.sucesso_associar`),
      sucesso_desassociar: this.translate.instant(`${base}.sucesso_desassociar`),
      opcoes: this.translate.instant(`${base}.opcoes`),
    };
  }
}
